import type {
  SuperDocConfig,
  SuperDocEditorCreateEvent,
  SuperDocExceptionEvent,
  SuperDocInstance,
  SuperDocModules,
  SuperDocRef,
  SuperDocZoomChangeEvent,
} from '@superdoc/react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { DraftDocxTableContextMenu } from '@/components/drafts/DraftDocxTableContextMenu';
import { Button } from '@/components/ui/NewButton';
import {
  arrayBufferToDocxFile,
  centerEditorScroll,
  exportSuperDocDocxBuffer,
  getEditorScrollContainer,
  isRecoverableSuperDocTableProjectionError,
  superDocErrorMessage,
  usePortalElementRect,
} from '@/components/drafts/draftDocxViewerUtils';
import Loading from '@/components/shared/Loading';
import { useDialogContainer } from '@/components/ui/dialog-container-context';
import { useBundleFonts } from '@/hooks/useBundleFonts';
import { apiErrorMessage } from '@/lib/api-client';
import { stripCrimsonNodeAnchorsFromDocx } from '@/lib/draftDocxNormalizer';
import { buildDraftSuperDocFontConfig } from '@/lib/draftSuperDocFonts';
import {
  installSuperDocTableResizeTargetFix,
} from '@/lib/superdocTableResizeTarget';
import { cn } from '@/lib/utils';

const SuperDocEditor = React.lazy(() =>
  import('@superdoc/react').then((mod) => ({ default: mod.SuperDocEditor })),
);

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export type DraftDocxRoomMode = 'create' | 'join';

interface DraftDocxPageState {
  currentPage: number;
  totalPages: number;
}

export interface DraftDocxAwarenessState {
  clientId?: number;
  color?: string;
  email?: string | null;
  name?: string | null;
}

export interface DraftDocxViewerCollaboration {
  /** Room identity; SuperDoc owns the provider and Y.Doc for it. */
  documentId: string;
  localUser?: {
    color?: string;
    email?: string | null;
    name: string;
  };
  onAwarenessUpdate?: (states: DraftDocxAwarenessState[]) => void;
  onCollaborationError?: (message: string) => void;
  onCollaborationReady?: () => void;
  /**
   * SuperDoc has no join-or-create mode, so a wrong guess is reported as a
   * terminal exception. The page flips the mode and remounts.
   */
  onRoomModeConflict?: (nextRoomMode: DraftDocxRoomMode) => void;
  params?: Record<string, string>;
  roomMode: DraftDocxRoomMode;
  serverUrl: string;
}

export interface DraftDocxEditorHandle {
  getSuperDoc(): SuperDocInstance | null;
  save(options?: { selective?: boolean }): Promise<ArrayBuffer | null>;
  scrollToChangeId?(revisionId: number | string): void;
  setZoom(zoom: number): void;
}

const DEFAULT_FIT_PADDING_PX = 48;
const DEFAULT_MIN_FIT_ZOOM = 0.45;
const DEFAULT_MAX_FIT_ZOOM = 1;
const MOUSE_WHEEL_ZOOM_MIN = 0.5;
const MOUSE_WHEEL_ZOOM_MAX = 2;
const MOUSE_WHEEL_ZOOM_MAX_DELTA = 120;
const MOUSE_WHEEL_ZOOM_SENSITIVITY = 0.003;
const ZOOM_CONTROL_STEP_PERCENT = 10;
const TEMPLATE_PREVIEW_MAX_FIT_ZOOM = 2;
const PAGE_INDICATOR_LINGER_MS = 1200;
const TABLE_PROJECTION_RECOVERY_LIMIT = 3;
// Viewing can virtualize. Editing must not: table resize/context-menu targeting
// maps mounted `.superdoc-table-fragment` nodes onto `tables.list()`, and a
// partial window remaps later tables onto earlier engine ids.
const DRAFT_SUPERDOC_LAYOUT_ENGINE_OPTIONS_VIEWING = {
  virtualization: {
    enabled: true,
    window: 3,
    overscan: 0,
  },
} satisfies NonNullable<SuperDocConfig['layoutEngineOptions']>;

const DRAFT_SUPERDOC_LAYOUT_ENGINE_OPTIONS_EDITING = {
  virtualization: {
    enabled: false,
  },
} satisfies NonNullable<SuperDocConfig['layoutEngineOptions']>;

const DRAFT_SUPERDOC_EXCLUDED_TOOLBAR_ITEMS = [
  'linkedStyles',
  'copyFormat',
  'clearFormatting',
  'documentMode',
  'ruler',
  'formattingMarks',
  'measurementUnit',
] as const;

const ROOM_MODE_CONFLICT_CODES: Record<string, DraftDocxRoomMode> = {
  'collaboration-v2-room-already-exists': 'join',
  'collaboration-v2-room-missing': 'create',
};

const DOCX_VIEWER_STYLES = `
  .draft-docx-editor {
    --sd-layout-page-shadow:
      0 22px 60px rgba(15, 23, 42, 0.10),
      0 0 0 1px rgba(148, 163, 184, 0.14);
    --draft-table-guide: rgba(100, 116, 139, 0.35);
    --draft-table-guide-strong: rgba(71, 85, 105, 0.65);
    background: #f5f7fa !important;
    height: 100%;
    min-height: 0;
  }
  .draft-docx-editor .superdoc,
  .draft-docx-editor .superdoc__layers,
  .draft-docx-editor .superdoc__document,
  .draft-docx-editor .superdoc__sub-document {
    height: 100%;
    min-height: 0;
  }
  .draft-docx-editor .superdoc__layers,
  .draft-docx-editor .superdoc__document {
    flex: 1 1 0;
    max-width: none !important;
    min-width: 0;
    width: 100% !important;
  }
  .draft-docx-editor .superdoc__sub-document {
    box-sizing: border-box;
    max-width: none !important;
    width: 100% !important;
  }
  /* SuperDoc pageGap is between pages only; this keeps page 1 off the toolbar. */
  .draft-docx-editor:not(.draft-docx-editor--fit-width):not(.title-page-preview):not(.title-page-editor-panel) .superdoc__sub-document {
    padding-top: 24px;
  }
  .draft-docx-editor--fit-width {
    --sd-layout-page-shadow:
      0 14px 36px rgba(15, 23, 42, 0.10),
      0 0 0 1px rgba(148, 163, 184, 0.16);
  }
  .draft-docx-editor--comparison [data-revision-id] {
    cursor: pointer !important;
  }
  /*
   * Word-style gridlines. SuperDoc paints cell borders as inline styles, so a
   * borderless table leaves no visual footprint. Outlines are layout-neutral,
   * and the resize overlay already spans every column and row boundary, so
   * tinting its guides marks the interior grid for free. Both reveal on hover,
   * because SuperDoc only mounts those guides for the table under the pointer.
   *
   * Leave the overlay pointer-visible so the blue column line still appears.
   * Our capture listener takes the drag before SuperDoc can commit the wrong
   * table.
   */
  .draft-docx-editor--editing .superdoc-table-fragment:hover {
    outline: 1px dashed var(--draft-table-guide-strong);
  }
  .draft-docx-editor--editing [data-sd-table-resize-overlay] .v2-table-resize-handle__line {
    background: var(--draft-table-guide);
  }
  .crimson-table-resize-guideline {
    pointer-events: none;
    position: fixed;
    z-index: 2147483646;
    width: 2px;
    margin-left: -1px;
    background: #2563eb;
  }
`;

const DEFAULT_FRAME_CLASSNAME =
  'draft-docx-editor h-[calc(100vh-220px)] min-h-[620px] overflow-hidden rounded-b-xl bg-slate-50';

const DEFAULT_PLACEHOLDER_CLASSNAME = 'h-[calc(100vh-220px)] min-h-[620px]';

const DEFAULT_EMPTY_CLASSNAME =
  'flex h-[calc(100vh-220px)] min-h-[620px] items-center justify-center bg-slate-50/80 px-6 text-center text-sm text-slate-500';

type SuperDocActiveEditor = NonNullable<SuperDocInstance['activeEditor']>;

const renderNoLoadingSurface = () => null;

function DraftDocxLoadingSurface() {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-50">
      <Loading />
    </div>
  );
}

function roomModeConflictFrom(event: unknown): DraftDocxRoomMode | null {
  if (!event || typeof event !== 'object') return null;
  const code = (event as { code?: unknown }).code;
  if (typeof code !== 'string') return null;
  return ROOM_MODE_CONFLICT_CODES[code] ?? null;
}

interface ActiveDocumentState {
  buffer: ArrayBuffer | null;
  reloadKey: number | string | null;
  revision: number;
  title: string;
}

export const DraftDocxViewer = React.memo(function DraftDocxViewer({
  buffer,
  bufferReloadKey,
  centerScroll = true,
  collaboration,
  documentId,
  editorInstanceKey = 0,
  editorRef,
  emptyClassName,
  error,
  fitMaxZoom,
  fitMinZoom = DEFAULT_MIN_FIT_ZOOM,
  fitPaddingPx,
  fitToWidth = false,
  frameClassName,
  initialZoom = 1,
  isComparison = false,
  isEditing = false,
  isLoading,
  onChange,
  placeholderClassName,
  portalVisible = true,
  portalZIndex = 20,
  renderInPlace = false,
  showPageIndicator = false,
  showZoomControls = false,
  stripCrimsonNodeAnchors = false,
  title,
  versionKey = 'latest',
}: {
  buffer: ArrayBuffer | null;
  bufferReloadKey?: number | string | null;
  /** When false, leave scroll at top-left (thumbnails). Default centers horizontally. */
  centerScroll?: boolean;
  collaboration?: DraftDocxViewerCollaboration | null;
  documentId: string;
  editorInstanceKey?: number;
  editorRef?: React.RefObject<DraftDocxEditorHandle | null>;
  emptyClassName?: string;
  error: unknown;
  fitMaxZoom?: number;
  fitMinZoom?: number;
  fitPaddingPx?: number;
  fitToWidth?: boolean;
  frameClassName?: string;
  initialZoom?: number;
  isComparison?: boolean;
  isEditing?: boolean;
  isLoading: boolean;
  onChange?: () => void;
  placeholderClassName?: string;
  portalVisible?: boolean;
  portalZIndex?: number;
  renderInPlace?: boolean;
  showPageIndicator?: boolean;
  showZoomControls?: boolean;
  stripCrimsonNodeAnchors?: boolean;
  title: string;
  versionKey?: number | string;
}) {
  const dialogContainer = useDialogContainer();
  const bundleFontOptions = useBundleFonts();
  const draftSuperDocFontConfig = React.useMemo(
    () => buildDraftSuperDocFontConfig(bundleFontOptions),
    [bundleFontOptions],
  );
  const portalContainer = dialogContainer ?? (typeof document === 'undefined' ? null : document.body);
  const internalEditorRef = React.useRef<DraftDocxEditorHandle>(null);
  const superDocRef = React.useRef<SuperDocRef>(null);
  const superDocInstanceRef = React.useRef<SuperDocInstance | null>(null);
  const superDocEditorRef = React.useRef<SuperDocActiveEditor | null>(null);
  const frameRef = React.useRef<HTMLDivElement>(null);
  const placeholderRef = React.useRef<HTMLDivElement>(null);
  const currentZoomPercentRef = React.useRef(Math.round(initialZoom * 100));
  const [zoomPercent, setZoomPercent] = React.useState(() => Math.round(initialZoom * 100));
  const pageStateRef = React.useRef<DraftDocxPageState>({ currentPage: 1, totalPages: 0 });
  const [pageState, setPageState] = React.useState<DraftDocxPageState>(pageStateRef.current);
  const [isPageIndicatorVisible, setIsPageIndicatorVisible] = React.useState(false);
  const pageIndicatorTimerRef = React.useRef<number | null>(null);
  const [activeDocument, setActiveDocument] = React.useState<ActiveDocumentState>(() => ({
    buffer,
    reloadKey: bufferReloadKey ?? null,
    revision: 0,
    title,
  }));
  const activeDocumentRef = React.useRef(activeDocument);
  const latestTitleRef = React.useRef(title);
  const onChangeRef = React.useRef(onChange);
  const [documentFile, setDocumentFile] = React.useState<File | null>(null);
  const [superDocRuntimeError, setSuperDocRuntimeError] = React.useState<string | null>(null);
  const [superDocReadyMountKey, setSuperDocReadyMountKey] = React.useState<string | null>(null);
  const [tableProjectionRecoveryNonce, setTableProjectionRecoveryNonce] = React.useState(0);
  const tableProjectionRecoveryRef = React.useRef(false);
  const tableProjectionRecoveryCountRef = React.useRef(0);
  const resolvedEditorRef = editorRef ?? internalEditorRef;
  const resolvedEmptyClassName = emptyClassName ?? DEFAULT_EMPTY_CLASSNAME;
  const activeBuffer = activeDocument.buffer;
  const documentRevisionKey = activeDocument.reloadKey ?? activeDocument.revision;
  // Keep SuperDoc in editing whenever the user can write. Flipping to viewing
  // (for example while a live room is still connecting) nulls the toolbar's
  // active editor and leaves undo/redo dead after the later switch back.
  // Empty rooms also stay editable so SuperDoc can seed Yjs from the DOCX.
  const shouldMountEditable = isEditing || collaboration?.roomMode === 'create';
  const resolvedFitMaxZoom = fitMaxZoom ?? (showZoomControls ? TEMPLATE_PREVIEW_MAX_FIT_ZOOM : DEFAULT_MAX_FIT_ZOOM);
  const resolvedFitPaddingPx = fitPaddingPx ?? (showZoomControls ? 16 : DEFAULT_FIT_PADDING_PX);
  const allowWheelZoom = isEditing || showZoomControls;
  const shouldMountEditorControls = shouldMountEditable || Boolean(collaboration);
  const shouldInstallReviewModules = Boolean(collaboration || isComparison);
  const editorMountKey = [
    documentId,
    versionKey,
    documentRevisionKey,
    editorInstanceKey,
    tableProjectionRecoveryNonce,
    collaboration ? `${collaboration.documentId}:${collaboration.roomMode}` : 'local',
  ].join('-');
  const latestEditorMountKeyRef = React.useRef(editorMountKey);
  latestEditorMountKeyRef.current = editorMountKey;
  const isSuperDocReady = superDocReadyMountKey === editorMountKey;
  const portalRect = usePortalElementRect(
    placeholderRef,
    Boolean(activeBuffer && !isLoading && !error && !renderInPlace),
    portalContainer,
  );

  const superDocModules = React.useMemo<SuperDocModules>(() => {
    const modules: SuperDocModules = {
      comments: isComparison
        // Review chrome stays visible in comparison mode but every accept /
        // reject / resolve affordance is rejected by SuperDoc itself.
        ? { readOnly: true }
        : false,
    };

    if (shouldInstallReviewModules) {
      modules.trackChanges = {
        enabled: true,
        mode: 'review' as const,
        visible: true,
      };
    }

    return modules;
  }, [isComparison, shouldInstallReviewModules]);

  const superDocUi = React.useMemo<NonNullable<SuperDocConfig['ui']>>(() => {
    const toolbarFonts = draftSuperDocFontConfig.toolbarFonts;

    return {
      comments: false,
      contextMenu: {
        menuProvider: (_context, sections) => sections
          .map((section) => ({
            ...section,
            items: section.items.filter((item) => item.id !== 'comment'),
          }))
          .filter((section) => section.items.length > 0),
      },
      ruler: false,
      ...(shouldInstallReviewModules ? { contentControls: { chrome: 'none' as const } } : {}),
      toolbar: shouldMountEditorControls
        ? {
          excludeItems: [...DRAFT_SUPERDOC_EXCLUDED_TOOLBAR_ITEMS],
          // SuperDoc's overflow menu does not honor excludeItems, so keep
          // those controls on the main bar where exclusion actually applies.
          hideButtons: false,
          responsiveToContainer: true,
          ...(toolbarFonts?.length ? { fonts: toolbarFonts } : {}),
        }
        : false,
    };
  }, [draftSuperDocFontConfig.toolbarFonts, shouldInstallReviewModules, shouldMountEditorControls]);

  const superDocDocument = React.useMemo(() => {
    if (!documentFile) return null;
    if (!collaboration) return documentFile;

    return {
      data: documentFile,
      id: collaboration.documentId,
      type: DOCX_MIME_TYPE,
      v2Collaboration: {
        documentId: collaboration.documentId,
        params: collaboration.params ?? null,
        providerType: 'y-websocket' as const,
        roomMode: collaboration.roomMode,
        serverUrl: collaboration.serverUrl,
      },
    };
  }, [collaboration, documentFile]);

  React.useEffect(() => {
    activeDocumentRef.current = activeDocument;
  }, [activeDocument]);

  React.useEffect(() => {
    latestTitleRef.current = title;
  }, [title]);

  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  React.useEffect(() => {
    const nextReloadKey = bufferReloadKey ?? null;
    const currentDocument = activeDocumentRef.current;
    const hasExplicitReloadKey = bufferReloadKey != null;
    const shouldReload = hasExplicitReloadKey
      ? (
        currentDocument.reloadKey !== nextReloadKey
        || (currentDocument.buffer === null && buffer !== null)
      )
      : currentDocument.buffer !== buffer;

    if (!shouldReload) return;

    const nextDocument = {
      buffer,
      reloadKey: nextReloadKey,
      revision: currentDocument.revision + 1,
      title,
    };
    activeDocumentRef.current = nextDocument;
    setActiveDocument(nextDocument);
    superDocInstanceRef.current = null;
    superDocEditorRef.current = null;
    tableProjectionRecoveryRef.current = false;
    tableProjectionRecoveryCountRef.current = 0;
    setTableProjectionRecoveryNonce(0);
    setSuperDocRuntimeError(null);
    setSuperDocReadyMountKey(null);
  }, [buffer, bufferReloadKey, title]);

  // Crimson anchor content controls are stripped from the DOCX rather than the
  // mounted document: v2 exposes no editor view to rewrite after load.
  React.useEffect(() => {
    if (!activeBuffer) {
      setDocumentFile(null);
      return undefined;
    }

    let cancelled = false;
    const documentTitle = activeDocument.title;

    const prepare = async () => {
      const prepared = stripCrimsonNodeAnchors
        ? await stripCrimsonNodeAnchorsFromDocx(activeBuffer)
        : activeBuffer;
      if (cancelled) return;
      setDocumentFile(arrayBufferToDocxFile(prepared, documentTitle));
    };

    void prepare();

    return () => {
      cancelled = true;
    };
  }, [activeBuffer, activeDocument.title, stripCrimsonNodeAnchors]);

  const setScopedZoomPercent = React.useCallback((zoomPercent: number) => {
    const superdoc = superDocInstanceRef.current ?? superDocRef.current?.getInstance();
    if (!superdoc?.setZoom) return false;

    currentZoomPercentRef.current = zoomPercent;
    setZoomPercent(Math.round(zoomPercent));
    superdoc.setZoom(zoomPercent);
    return true;
  }, []);

  const applyZoomStep = React.useCallback((deltaPercent: number) => {
    const nextZoomPercent = Math.min(
      MOUSE_WHEEL_ZOOM_MAX * 100,
      Math.max(MOUSE_WHEEL_ZOOM_MIN * 100, currentZoomPercentRef.current + deltaPercent),
    );
    setScopedZoomPercent(nextZoomPercent);
  }, [setScopedZoomPercent]);

  const getTableMenuSource = React.useCallback(() => (
    superDocInstanceRef.current
    ?? superDocRef.current?.getInstance()
    ?? superDocEditorRef.current
  ), []);
  const getTableMenuStage = React.useCallback(() => frameRef.current, []);
  const handleTableMenuMutated = React.useCallback(() => onChangeRef.current?.(), []);
  const uninstallTableResizeRef = React.useRef<(() => void) | null>(null);
  const installTableResizeTargetFix = React.useCallback(() => {
    uninstallTableResizeRef.current?.();
    uninstallTableResizeRef.current = installSuperDocTableResizeTargetFix({
      getSource: () => (
        superDocEditorRef.current
        ?? superDocInstanceRef.current?.activeEditor
        ?? superDocInstanceRef.current
      ),
      getStage: () => frameRef.current,
      onMutated: () => onChangeRef.current?.(),
    });
  }, []);

  React.useEffect(() => () => {
    uninstallTableResizeRef.current?.();
    uninstallTableResizeRef.current = null;
  }, []);

  const handleSuperDocReady = React.useCallback(({ superdoc }: { superdoc: SuperDocInstance }) => {
    if (latestEditorMountKeyRef.current !== editorMountKey) return;

    superDocInstanceRef.current = superdoc;
    tableProjectionRecoveryRef.current = false;
    installTableResizeTargetFix();
    setSuperDocRuntimeError(null);
    setSuperDocReadyMountKey(editorMountKey);
    if (centerScroll) centerEditorScroll(frameRef.current);
  }, [centerScroll, editorMountKey, installTableResizeTargetFix]);

  const handleZoomChange = React.useCallback((event: SuperDocZoomChangeEvent) => {
    if (Number.isFinite(event.zoom)) {
      currentZoomPercentRef.current = event.zoom;
      setZoomPercent(Math.round(event.zoom));
    }
  }, []);

  const handleSuperDocContentError = React.useCallback((event: unknown) => {
    const message = superDocErrorMessage(event);
    console.error('SuperDoc failed to load DOCX content', event);
    setSuperDocRuntimeError(message);
  }, []);

  const handleSuperDocException = React.useCallback((event: SuperDocExceptionEvent) => {
    const conflictRoomMode = roomModeConflictFrom(event);
    if (conflictRoomMode && collaboration?.onRoomModeConflict) {
      collaboration.onRoomModeConflict(conflictRoomMode);
      return;
    }

    const message = superDocErrorMessage(event);
    if (collaboration && isRecoverableSuperDocTableProjectionError(message)) {
      if (tableProjectionRecoveryRef.current) return;
      if (tableProjectionRecoveryCountRef.current >= TABLE_PROJECTION_RECOVERY_LIMIT) {
        console.error('SuperDoc table projection recovery exhausted', event);
        collaboration.onCollaborationError?.(message);
        setSuperDocRuntimeError(message);
        return;
      }

      tableProjectionRecoveryRef.current = true;
      tableProjectionRecoveryCountRef.current += 1;
      superDocInstanceRef.current = null;
      superDocEditorRef.current = null;
      setSuperDocReadyMountKey(null);
      console.warn('SuperDoc table projection drifted; rejoining the live room', message);

      // Pause live export and remount from Yjs. The projector lost the table
      // span after delete; the room still has the undone document.
      const alreadyJoining = collaboration.roomMode === 'join';
      collaboration.onRoomModeConflict?.('join');
      if (alreadyJoining || !collaboration.onRoomModeConflict) {
        setTableProjectionRecoveryNonce((nonce) => nonce + 1);
      }
      return;
    }

    console.error('SuperDoc editor exception', event);
    collaboration?.onCollaborationError?.(message);
    setSuperDocRuntimeError(message);
  }, [collaboration]);

  const handleAwarenessUpdate = React.useCallback(({ states }: { states: DraftDocxAwarenessState[] }) => {
    collaboration?.onAwarenessUpdate?.(states);
  }, [collaboration]);

  const handleCollaborationReady = React.useCallback(() => {
    installTableResizeTargetFix();
    collaboration?.onCollaborationReady?.();
  }, [collaboration, installTableResizeTargetFix]);

  const handleEditorCreate = React.useCallback(({ editor }: SuperDocEditorCreateEvent) => {
    superDocEditorRef.current = editor as SuperDocActiveEditor;
    installTableResizeTargetFix();
  }, [installTableResizeTargetFix]);

  const handleEditorUpdate = React.useCallback(() => {
    onChange?.();
  }, [onChange]);

  const handlePaginationUpdate = React.useCallback(({ totalPages }: { totalPages: number }) => {
    if (totalPages === pageStateRef.current.totalPages) return;
    pageStateRef.current = { ...pageStateRef.current, totalPages };
    setPageState(pageStateRef.current);
  }, []);

  // The indicator is a scroll affordance, so it surfaces on movement and then
  // fades back out rather than permanently occupying space over the page.
  const revealPageIndicator = React.useCallback(() => {
    setIsPageIndicatorVisible(true);
    if (pageIndicatorTimerRef.current !== null) {
      window.clearTimeout(pageIndicatorTimerRef.current);
    }
    pageIndicatorTimerRef.current = window.setTimeout(() => {
      pageIndicatorTimerRef.current = null;
      setIsPageIndicatorVisible(false);
    }, PAGE_INDICATOR_LINGER_MS);
  }, []);

  React.useEffect(() => () => {
    if (pageIndicatorTimerRef.current !== null) {
      window.clearTimeout(pageIndicatorTimerRef.current);
    }
  }, []);

  // `observe` covers scroll, zoom, resize, repagination and virtualized page
  // mounts, so the page readout stays correct without a scroll listener.
  React.useEffect(() => {
    if (!isSuperDocReady || !showPageIndicator) return undefined;

    const viewport = superDocInstanceRef.current?.ui?.viewport;
    if (!viewport) return undefined;

    const readCurrentPage = () => {
      const host = viewport.getHost();
      const scrollContainer = getEditorScrollContainer(frameRef.current);
      if (!host || !scrollContainer) return;

      const viewportTop = scrollContainer.getBoundingClientRect().top;
      const pages = host.querySelectorAll<HTMLElement>('.superdoc-page[data-page-index]');

      for (const page of pages) {
        if (page.getBoundingClientRect().bottom <= viewportTop + 1) continue;

        const pageIndex = Number(page.dataset.pageIndex);
        if (!Number.isFinite(pageIndex)) return;

        const currentPage = pageIndex + 1;
        if (currentPage === pageStateRef.current.currentPage) return;

        pageStateRef.current = { ...pageStateRef.current, currentPage };
        setPageState(pageStateRef.current);
        return;
      }
    };

    // `observe` also fires for zoom and resize, so the indicator is tied to a
    // real scroll offset change to keep it from flashing while the user types.
    let lastScrollTop: number | null = null;
    const handleViewportChange = () => {
      const scrollTop = getEditorScrollContainer(frameRef.current)?.scrollTop ?? null;
      if (scrollTop !== null && lastScrollTop !== null && scrollTop !== lastScrollTop) {
        revealPageIndicator();
      }
      lastScrollTop = scrollTop;
      readCurrentPage();
    };

    handleViewportChange();
    return viewport.observe(handleViewportChange);
  }, [isSuperDocReady, revealPageIndicator, showPageIndicator]);

  React.useImperativeHandle(resolvedEditorRef, () => ({
    getSuperDoc: () => superDocInstanceRef.current ?? superDocRef.current?.getInstance() ?? null,
    save: async () => {
      const superdoc = superDocInstanceRef.current ?? superDocRef.current?.getInstance();
      if (!superdoc) return null;
      return await exportSuperDocDocxBuffer(superdoc);
    },
    scrollToChangeId: (revisionId: number | string) => {
      const superdoc = superDocInstanceRef.current ?? superDocRef.current?.getInstance();
      // A stale or unpainted id resolves to `success: false` forever, so the
      // single bounded reveal step SuperDoc performs here is deliberate.
      void superdoc?.ui.trackChanges.scrollTo(String(revisionId));
    },
    setZoom: (zoom: number) => {
      setScopedZoomPercent(Math.round(zoom * 100));
    },
  }), [setScopedZoomPercent]);

  React.useEffect(() => {
    if (!activeBuffer || isLoading || error || fitToWidth) return undefined;

    const timeout = window.setTimeout(() => {
      resolvedEditorRef.current?.setZoom(initialZoom);
      if (centerScroll) centerEditorScroll(frameRef.current);
    }, 0);
    const animationFrame = window.requestAnimationFrame(() => {
      if (centerScroll) centerEditorScroll(frameRef.current);
    });

    return () => {
      window.clearTimeout(timeout);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [activeBuffer, centerScroll, error, fitToWidth, initialZoom, isLoading, resolvedEditorRef]);

  const applyMouseWheelZoom = React.useCallback((event: WheelEvent) => {
    const deltaMultiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE
      ? 16
      : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
        ? 100
        : 1;
    const normalizedDelta = Math.min(
      MOUSE_WHEEL_ZOOM_MAX_DELTA,
      Math.max(-MOUSE_WHEEL_ZOOM_MAX_DELTA, event.deltaY * deltaMultiplier),
    );
    const currentZoom = currentZoomPercentRef.current / 100;
    const nextZoom = currentZoom * Math.exp(-normalizedDelta * MOUSE_WHEEL_ZOOM_SENSITIVITY);
    const clampedZoom = Math.min(MOUSE_WHEEL_ZOOM_MAX, Math.max(MOUSE_WHEEL_ZOOM_MIN, nextZoom));
    const nextZoomPercent = clampedZoom * 100;
    const scrollContainer = getEditorScrollContainer(frameRef.current);
    const scrollRect = scrollContainer?.getBoundingClientRect();
    const anchor = scrollContainer && scrollRect
      ? {
        element: scrollContainer,
        offsetX: event.clientX - scrollRect.left,
        offsetY: event.clientY - scrollRect.top,
        scrollLeft: scrollContainer.scrollLeft,
        scrollTop: scrollContainer.scrollTop,
      }
      : null;

    if (!setScopedZoomPercent(nextZoomPercent)) return;

    if (anchor && currentZoom > 0) {
      const zoomRatio = clampedZoom / currentZoom;
      window.requestAnimationFrame(() => {
        anchor.element.scrollLeft = Math.max(0, (anchor.scrollLeft + anchor.offsetX) * zoomRatio - anchor.offsetX);
        anchor.element.scrollTop = Math.max(0, (anchor.scrollTop + anchor.offsetY) * zoomRatio - anchor.offsetY);
      });
    }
  }, [setScopedZoomPercent]);

  React.useEffect(() => {
    if (!activeBuffer || isLoading || error) return undefined;
    const frame = frameRef.current;
    if (!frame) return undefined;

    const handleNativeWheelZoom = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey) || !allowWheelZoom) return;
      event.preventDefault();
      event.stopPropagation();
      applyMouseWheelZoom(event);
    };

    frame.addEventListener('wheel', handleNativeWheelZoom, { capture: true, passive: false });
    return () => {
      frame.removeEventListener('wheel', handleNativeWheelZoom, { capture: true });
    };
  }, [activeBuffer, allowWheelZoom, applyMouseWheelZoom, error, isLoading]);

  if (isLoading) {
    return (
      <div className={resolvedEmptyClassName}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={resolvedEmptyClassName}>
        {apiErrorMessage(error) || 'Failed to load DOCX preview'}
      </div>
    );
  }

  if (!activeBuffer) {
    return (
      <div className={resolvedEmptyClassName}>
        No DOCX preview available.
      </div>
    );
  }

  const editorSurface = (
    <div
      ref={frameRef}
      className={cn(
        frameClassName ?? DEFAULT_FRAME_CLASSNAME,
        'relative',
        shouldMountEditable && 'draft-docx-editor--editing',
        fitToWidth && 'draft-docx-editor--fit-width',
        isComparison && 'draft-docx-editor--comparison',
      )}
    >
      <style>{DOCX_VIEWER_STYLES}</style>
      {superDocRuntimeError ? (
        <div className="flex h-full items-center justify-center bg-slate-50/80 px-6 text-center text-sm text-slate-500">
          {superDocRuntimeError}
        </div>
      ) : superDocDocument ? (
        <React.Suspense fallback={null}>
          <SuperDocEditor
            key={editorMountKey}
            ref={superDocRef}
            allowSelectionInViewMode
            className={cn(
              'h-full min-h-0 transition-opacity duration-150',
              !isSuperDocReady && 'opacity-0',
            )}
            contained
            document={superDocDocument}
            documentMode={shouldMountEditable ? 'editing' : 'viewing'}
            fonts={draftSuperDocFontConfig.fonts}
            layoutEngineOptions={
              shouldMountEditable
                ? DRAFT_SUPERDOC_LAYOUT_ENGINE_OPTIONS_EDITING
                : DRAFT_SUPERDOC_LAYOUT_ENGINE_OPTIONS_VIEWING
            }
            modules={superDocModules}
            onAwarenessUpdate={handleAwarenessUpdate}
            onCollaborationReady={handleCollaborationReady}
            onContentError={handleSuperDocContentError}
            onEditorCreate={handleEditorCreate}
            onEditorUpdate={handleEditorUpdate}
            onException={handleSuperDocException}
            onPaginationUpdate={handlePaginationUpdate}
            onReady={handleSuperDocReady}
            onZoomChange={handleZoomChange}
            renderLoading={renderNoLoadingSurface}
            role={shouldMountEditorControls ? 'editor' : 'viewer'}
            telemetry={{ enabled: false }}
            title={title}
            ui={superDocUi}
            user={collaboration?.localUser ?? { name: 'Crimson user' }}
            zoom={{
              fitWidth: {
                max: Math.round(resolvedFitMaxZoom * 100),
                min: Math.round(fitMinZoom * 100),
                padding: resolvedFitPaddingPx,
              },
              initial: Math.round(initialZoom * 100),
              mode: fitToWidth ? 'fit-width' : 'manual',
            }}
          />
        </React.Suspense>
      ) : null}
      {!superDocRuntimeError && !isSuperDocReady ? <DraftDocxLoadingSurface /> : null}
      {showZoomControls && isSuperDocReady ? (
        <div className="absolute right-3 top-3 z-20 flex items-center gap-1 rounded-md border border-slate-200 bg-white/90 p-1 shadow-sm backdrop-blur-sm">
          <Button
            aria-label="Zoom out"
            disabled={zoomPercent <= MOUSE_WHEEL_ZOOM_MIN * 100}
            icon={ZoomOut}
            onClick={() => applyZoomStep(-ZOOM_CONTROL_STEP_PERCENT)}
            size="xs"
            tooltip="Zoom out"
            variant="secondary"
          />
          <span className="min-w-12 px-1 text-center text-xs tabular-nums text-slate-600">
            {`${Math.round(zoomPercent)}%`}
          </span>
          <Button
            aria-label="Zoom in"
            disabled={zoomPercent >= MOUSE_WHEEL_ZOOM_MAX * 100}
            icon={ZoomIn}
            onClick={() => applyZoomStep(ZOOM_CONTROL_STEP_PERCENT)}
            size="xs"
            tooltip="Zoom in"
            variant="secondary"
          />
        </div>
      ) : null}
      {shouldMountEditable ? (
        <DraftDocxTableContextMenu
          enabled={isSuperDocReady}
          getSource={getTableMenuSource}
          getStage={getTableMenuStage}
          onMutated={handleTableMenuMutated}
        />
      ) : null}
      {showPageIndicator && isSuperDocReady && pageState.totalPages > 0 ? (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute bottom-5 left-1/2 z-20 -translate-x-1/2',
            'rounded-full bg-slate-900/70 px-3 py-1 text-xs font-medium tabular-nums text-white',
            'shadow-sm backdrop-blur-sm transition-opacity duration-300 ease-out',
            isPageIndicatorVisible ? 'opacity-100' : 'opacity-0',
          )}
        >
          {`Page ${Math.min(pageState.currentPage, pageState.totalPages)} of ${pageState.totalPages}`}
        </div>
      ) : null}
    </div>
  );

  if (renderInPlace) return editorSurface;

  return (
    <>
      <div
        ref={placeholderRef}
        className={placeholderClassName ?? DEFAULT_PLACEHOLDER_CLASSNAME}
        aria-hidden="true"
      />
      {portalRect
        ? createPortal(
          <div style={{ ...portalRect, visibility: portalVisible ? undefined : 'hidden', zIndex: portalZIndex }}>
            {editorSurface}
          </div>,
          portalContainer ?? document.body,
        )
        : null}
    </>
  );
});
